import { when } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targetable, Targetables, Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

interface IUseUniqueReachableEmailsProps {
  targetings: ListResponse<Targeting>;
  campaign: Campaign;
}

export interface IUniqueEmailsResult {
  numUniqueEmails: number | null;
  isLoading: boolean;
}

interface IEmailListData {
  emailListUrls: string[];
  includedSegmentIds: string[];
  excludedSegmentIds: string[];
}

// Helper function to extract email list URLs from targetings
function extractEmailListUrls(
  emailListTargetings: Targeting[],
  Targetables: Targetables,
): string[] {
  return emailListTargetings
    .map((t) => {
      const targetableId = t.get('target_data')!;
      const targetable = Targetables.get(targetableId);
      return targetable?.get('list_file');
    })
    .filter(Boolean) as string[];
}

// Helper function to extract segment IDs from targetings
function extractSegmentIds(targetings: Targeting[]): {
  includedSegmentIds: string[];
  excludedSegmentIds: string[];
} {
  const includedSegmentIds = targetings
    .filter((t) => !t.get('is_archived') && t.get('kind') === 'segment' && t.get('included'))
    .map((t) => t.get('target_data'))
    .filter(Boolean) as string[];

  const excludedSegmentIds = targetings
    .filter((t) => !t.get('is_archived') && t.get('kind') === 'segment' && !t.get('included'))
    .map((t) => t.get('target_data'))
    .filter(Boolean) as string[];

  return { includedSegmentIds, excludedSegmentIds };
}

// Helper function to process email list data
function processEmailListData(targetings: Targeting[], Targetables: Targetables): IEmailListData {
  const emailListTargetings = targetings.filter(
    (t) => !t.get('is_archived') && t.get('kind') === 'email_list' && t.get('included'),
  );

  const emailListUrls = extractEmailListUrls(emailListTargetings, Targetables);
  const { includedSegmentIds, excludedSegmentIds } = extractSegmentIds(targetings);

  return {
    emailListUrls,
    includedSegmentIds,
    excludedSegmentIds,
  };
}

// Helper function to handle API call success
function handleApiSuccess(
  response: { unique_emails: number },
  setNumUniqueEmails: (value: number | null) => void,
): void {
  setNumUniqueEmails(response.unique_emails);
}

// Helper function to handle API call error
function handleApiError(
  error: any,
  t: (key: string, options?: any) => string,
  setNumUniqueEmails: (value: number | null) => void,
): void {
  toast(
    t('Unable to calculate unique email count due to a connection error. Please try again.', {
      error,
    }),
    {
      type: 'error',
    },
  );
  setNumUniqueEmails(null);
}

// Helper function to handle API call completion
function handleApiCompletion(setIsLoading: (value: boolean) => void): void {
  setIsLoading(false);
}

// Helper function to wait for targetables to load
async function waitForTargetablesToLoad(targetablesToWaitFor: Targetable[]): Promise<void> {
  if (targetablesToWaitFor.length > 0) {
    await Promise.all(targetablesToWaitFor.map((targetable) => when(() => !targetable.isPending)));
  }
}

// Helper function to make the API call
async function makeUniqueEmailsApiCall(
  emailListData: IEmailListData,
  Segments: any,
  t: (key: string, options?: any) => string,
  setNumUniqueEmails: (value: number | null) => void,
  setIsLoading: (value: boolean) => void,
): Promise<void> {
  const { emailListUrls, includedSegmentIds, excludedSegmentIds } = emailListData;

  if (includedSegmentIds.length === 0) {
    return;
  }

  setIsLoading(true);

  try {
    const response = await Segments.getUniqueEmailsFromTargetables({
      email_list_urls: emailListUrls,
      excluded_segment_ids: excludedSegmentIds,
      included_segment_ids: includedSegmentIds,
    });
    handleApiSuccess(response, setNumUniqueEmails);
  } catch (error) {
    handleApiError(error, t, setNumUniqueEmails);
  } finally {
    handleApiCompletion(setIsLoading);
  }
}

// This hook is similar to useReconcileUniqueEmails, but it only returns the number of unique emails and loading state.
// It is used to create the reach percentage in the campaign report.
export function useUniqueReachableEmails({
  targetings,
  campaign,
}: IUseUniqueReachableEmailsProps): IUniqueEmailsResult {
  const store = useStore();
  const { Segments, Targetables } = store;
  const [numUniqueEmails, setNumUniqueEmails] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(targetings.isPending);
  const { t } = useTranslation();
  const { isEmailMappingCampaign } = campaign;

  useEffect(() => {
    if (!isEmailMappingCampaign || targetings.isPending || targetings.models.length === 0) {
      setNumUniqueEmails(null);
      setIsLoading(false);
      return;
    }

    async function loadDataAndMakeApiCall(): Promise<void> {
      // Load the specific targetables that are referenced in email list targetings
      const emailListTargetings = targetings.models.filter(
        (t) => !t.get('is_archived') && t.get('kind') === 'email_list' && t.get('included'),
      );

      // Load each targetable (Collection.get() will automatically fetch if needed)
      const targetablesToWaitFor = emailListTargetings.map((t) => {
        const targetableId = t.get('target_data')!;
        return Targetables.get(targetableId);
      });

      // Wait for all targetables to finish loading (prevent missing email lists)
      await waitForTargetablesToLoad(targetablesToWaitFor);

      const emailListData = processEmailListData(targetings.models, Targetables);
      await makeUniqueEmailsApiCall(emailListData, Segments, t, setNumUniqueEmails, setIsLoading);
    }

    loadDataAndMakeApiCall();
  }, [targetings.isPending]);

  return {
    numUniqueEmails,
    isLoading,
  };
}
